import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Loader2, ArrowRight, ShieldCheck, AlertTriangle } from 'lucide-react';

const getToken = () => new URLSearchParams(window.location.search).get('token') || '';

const SERIF = "'Cormorant Garamond', 'Playfair Display', Georgia, serif";
const GOLD = '#D4AF6A';
const GOLD_GRAD = 'linear-gradient(135deg, #E8D29A 0%, #D4AF6A 52%, #BE933F 100%)';
const LOGIN_BG = '/themes/login-bg-A.png';

/**
 * Magic-link confirmation page (/auth/magic?token=...).
 *
 * The member clicks "Enter the Library", which fires a same-origin POST to
 * /api/auth/member/verify-magic-link to consume the single-use token. We do NOT
 * auto-consume on load: email scanners (Proofpoint, Outlook SafeLinks) GET-prefetch
 * links but never click buttons or POST, so the token survives until a real human
 * acts. A non-consuming GET /magic-preview lets us greet them by name first.
 */
const AuthMagic = () => {
  const [token] = useState(getToken);
  const [status, setStatus] = useState(token ? 'idle' : 'no_token'); // idle | loading | error | no_token
  const [errorMsg, setErrorMsg] = useState('');
  const [firstName, setFirstName] = useState('');

  // Non-consuming peek so we can greet by name (safe from scanner prefetch).
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/auth/member/magic-preview?token=${encodeURIComponent(token)}`);
        const data = await res.json().catch(() => ({}));
        if (!cancelled && data && data.first_name) setFirstName(data.first_name);
      } catch (e) { /* greeting is optional — ignore */ }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSignIn = useCallback(async () => {
    if (!token) { setStatus('no_token'); return; }
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch('/api/auth/member/verify-magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        window.location.replace(data.redirect || '/?welcome=1');
        return;
      }
      setErrorMsg(data.detail || 'This sign-in link is no longer valid.');
      setStatus('error');
    } catch (e) {
      setErrorMsg("We couldn't reach the server. Please check your connection and try again.");
      setStatus('error');
    }
  }, [token]);

  const isError = status === 'error' || status === 'no_token';
  const errorText = status === 'no_token'
    ? 'This sign-in link is missing or incomplete. Please request a fresh one.'
    : errorMsg;

  return (
    <div
      data-testid="auth-magic-page"
      style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px', position: 'relative', overflow: 'hidden', background: '#0B0E14',
        backgroundImage: `url(${LOGIN_BG})`, backgroundSize: 'cover', backgroundPosition: 'center',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* Dark veil so the gold waves stay subtle and the card reads cleanly */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'linear-gradient(180deg, rgba(8,11,18,0.74) 0%, rgba(8,11,18,0.84) 55%, rgba(8,11,18,0.92) 100%)',
      }} />
      {/* Warm gold glow behind the card */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        width: 620, height: 620, borderRadius: '50%', pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(212,175,106,0.16), rgba(8,11,18,0) 64%)',
      }} />

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        style={{
          position: 'relative', width: '100%', maxWidth: 452,
          background: 'rgba(13,18,28,0.62)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)',
          border: '1px solid rgba(212,175,106,0.22)', borderRadius: 20,
          boxShadow: '0 30px 70px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)',
          padding: '44px 40px 38px', textAlign: 'center',
        }}
      >
        <img
          src="/themes/ipmi50-logo-white.png"
          alt="IPMI 50"
          style={{ height: 46, marginBottom: 26, objectFit: 'contain' }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />

        <div style={{
          fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: GOLD, fontWeight: 600, marginBottom: 14,
        }}>
          IPMI Library
        </div>

        {!isError ? (
          <>
            <h1 style={{
              margin: '0 0 12px', fontFamily: SERIF, fontSize: 38, fontWeight: 500,
              color: '#FBF7EF', letterSpacing: '-0.01em', lineHeight: 1.08,
            }}>
              {firstName ? `Welcome, ${firstName}.` : "You're almost in."}
            </h1>
            <p style={{ margin: '0 0 30px', fontSize: 15, lineHeight: 1.6, color: 'rgba(232,226,214,0.62)' }}>
              You're one tap from the Intelligence Library. Confirm it's you to finish signing in.
            </p>

            <button
              type="button"
              onClick={handleSignIn}
              disabled={status === 'loading'}
              data-testid="auth-magic-signin-btn"
              style={{
                width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                gap: 10, background: GOLD_GRAD, color: '#1A1303', border: 'none',
                fontSize: 15.5, fontWeight: 700, letterSpacing: '0.01em',
                padding: '15px 24px', borderRadius: 12,
                cursor: status === 'loading' ? 'default' : 'pointer',
                opacity: status === 'loading' ? 0.8 : 1,
                boxShadow: '0 10px 28px rgba(212,175,106,0.28)',
                transition: 'transform 0.12s ease, box-shadow 0.2s ease, filter 0.2s ease',
              }}
              onMouseEnter={(e) => { if (status !== 'loading') { e.currentTarget.style.filter = 'brightness(1.05)'; e.currentTarget.style.boxShadow = '0 14px 34px rgba(212,175,106,0.4)'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(212,175,106,0.28)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {status === 'loading'
                ? (<><Loader2 size={18} style={{ animation: 'kb-spin 0.8s linear infinite' }} /> Signing you in&hellip;</>)
                : (<>Enter the Intelligence Library <ArrowRight size={18} /></>)}
            </button>

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              marginTop: 24, fontSize: 12.5, color: 'rgba(232,226,214,0.5)',
            }}>
              <ShieldCheck size={14} style={{ color: GOLD }} />
              Secure, single-use link &middot; expires in 15 minutes
            </div>
          </>
        ) : (
          <>
            <div style={{
              width: 54, height: 54, borderRadius: '50%', margin: '0 auto 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)',
            }}>
              <AlertTriangle size={25} style={{ color: '#F87171' }} />
            </div>
            <h1 style={{
              margin: '0 0 12px', fontFamily: SERIF, fontSize: 30, fontWeight: 500,
              color: '#FBF7EF', letterSpacing: '-0.01em', lineHeight: 1.12,
            }}>
              This link can't be used
            </h1>
            <p data-testid="auth-magic-error" style={{ margin: '0 0 28px', fontSize: 14.5, lineHeight: 1.6, color: 'rgba(232,226,214,0.62)' }}>
              {errorText}
            </p>
            <a
              href="/"
              data-testid="auth-magic-request-new"
              style={{
                width: '100%', boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', gap: 10, background: GOLD_GRAD, color: '#1A1303',
                textDecoration: 'none', fontSize: 15.5, fontWeight: 700, padding: '15px 24px',
                borderRadius: 12, boxShadow: '0 10px 28px rgba(212,175,106,0.28)',
              }}
            >
              Request a new sign-in link <ArrowRight size={18} />
            </a>
          </>
        )}
      </motion.div>

      <style>{`@keyframes kb-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AuthMagic;
